// src/components/character/printCharacterSheet.js
import React from "react";
import ReactDOMServer from "react-dom/server";
import { toast } from "react-toastify";
import QRCode from "qrcode";
import PrintableCharacterSheet from "./PrintableCharacterSheet";
import { buildCharacterQrPayload } from "./characterQrPayload";
import { encodeCharacterId } from "./characterSlug";
import * as api from "../../utils/api";

/** Resolve the public URL base (works in CRA & Vite) */
const BASE =
    (typeof import.meta !== "undefined" && import.meta.env && import.meta.env.BASE_URL) ||
    (typeof process !== "undefined" && process.env && process.env.PUBLIC_URL) ||
    "";

/** Fetch the CSS file as text once and cache it */
let __printCssCache = null;
async function getPrintCssText() {
    if (__printCssCache) return __printCssCache;
    const url = `${BASE.replace(/\/$/, "")}/print/character-sheet.print.css`;
    const res = await fetch(url, { cache: "no-store" });
    if (!res.ok) throw new Error(`Failed to load print CSS: ${res.status}`);
    __printCssCache = await res.text();
    return __printCssCache;
}

const GOOGLE_FONTS_HREF =
    "https://fonts.googleapis.com/css2?family=Cinzel:wght@400;700&family=Cinzel+Decorative:wght@400;700&display=swap";

/**
 * Resolves a qrCodeId server-side for the character's current build (see
 * POST /qrCode — reuses an existing id if one already points at this same
 * build) and renders it as a QR data URL. Returns null (after toasting) if
 * anything fails — the sheet still renders, just without a scannable code.
 */
export async function buildSheetQrDataUrl(character, { printedByPlayerId }) {
    const qrPayload = buildCharacterQrPayload(character);
    if (!qrPayload) return null;

    try {
        const { qrCodeId } = await api.createQrCode({
            characterId: character.characterId,
            payload: { ...qrPayload, printedByPlayerId, printedAt: Date.now() },
        });
        // The QR code only needs to encode this id — the full build data it
        // points to is resolved later via GET /qrCode/{qrCodeId}. This keeps
        // the printed code small enough to scan from a photo of the whole
        // sheet, not just a close-up of the code itself.
        return await QRCode.toDataURL(qrCodeId, {
            errorCorrectionLevel: "L",
            margin: 1,
            width: 450, // matches the 50%-larger .pcs-qr-img print size (0.65in -> 0.975in)
        });
    } catch (err) {
        console.error("Failed to generate character QR code:", err);
        toast.error("Failed to generate QR code — continuing without it.");
        return null;
    }
}

/** Renders the full standalone HTML document (doctype..html) for a character's printable sheet. */
async function buildSheetHtmlDocument({ character, races, skillsets, availableSkills, qrDataUrl }) {
    const bodyMarkup = ReactDOMServer.renderToStaticMarkup(
        <PrintableCharacterSheet
            character={character}
            races={races}
            skillsets={skillsets}
            availableSkills={availableSkills}
            qrDataUrl={qrDataUrl}
        />
    );
    const cssText = await getPrintCssText();

    return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>${character?.name ? `${character.name} — ` : ""}Character Sheet</title>
    <!-- Preconnects help Google Fonts start faster -->
    <link rel="preconnect" href="https://fonts.googleapis.com">
    <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
    <link rel="stylesheet" href="${GOOGLE_FONTS_HREF}" data-google-fonts>
    <style>${cssText}</style>
  </head>
  <body class="theme-print">
    ${bodyMarkup}
  </body>
</html>`;
}

/** Build a hidden iframe, load the HTML doc, wait for fonts, print, then clean up. */
async function printHtmlDocument(html) {
    const iframe = document.createElement("iframe");
    Object.assign(iframe.style, {
        position: "fixed",
        top: "-10000px",
        left: "-10000px",
        width: "1px",
        height: "1px",
        opacity: "0",
        pointerEvents: "none",
        border: "0",
    });
    document.body.appendChild(iframe);

    const onFrameLoad = async () => {
        const win = iframe.contentWindow;
        const doc = win.document;

        // The Google Fonts stylesheet is already linked in the doc's <head>
        // (see buildSheetHtmlDocument) — wait for it to finish loading
        // (with a timeout fallback) before printing.
        const link = doc.querySelector("link[data-google-fonts]");
        const stylesheetLoaded = new Promise((resolve) => {
            if (link && link.sheet) { resolve(); return; }
            let done = false;
            const finish = () => { if (!done) { done = true; resolve(); } };
            if (link) {
                link.addEventListener("load", finish, { once: true });
                link.addEventListener("error", finish, { once: true });
            }
            setTimeout(finish, 1500);
        });
        await stylesheetLoaded;

        // Ensure React had time to render & layout so the font faces are actually referenced
        await new Promise((r) => win.requestAnimationFrame(() => win.requestAnimationFrame(r)));

        // Explicitly request both weights so bold text uses Cinzel Decorative too
        try {
            if (doc.fonts && doc.fonts.load) {
                await Promise.allSettled([
                    doc.fonts.load('400 16px "Cinzel Decorative"'),
                    doc.fonts.load('700 16px "Cinzel Decorative"'),
                    doc.fonts.load('400 16px "Cinzel"'),
                    doc.fonts.load('700 16px "Cinzel"'),
                ]);
                await doc.fonts.ready;
            } else {
                await new Promise((r) => setTimeout(r, 300));
            }
        } catch {
            /* ignore font load errors */
        }

        // Print and cleanup
        try { win.focus(); } catch {}
        win.print();
        setTimeout(() => iframe.remove(), 1000);
    };

    iframe.addEventListener("load", onFrameLoad, { once: true });
    iframe.srcdoc = html;
}

/** Public API: print the sheet (hidden iframe -> browser print dialog). */
export async function printCharacterSheet({ character, races, skillsets, availableSkills, printedByPlayerId }) {
    const qrDataUrl = await buildSheetQrDataUrl(character, { printedByPlayerId });
    const html = await buildSheetHtmlDocument({ character, races, skillsets, availableSkills, qrDataUrl });
    await printHtmlDocument(html);
}

/**
 * Public API: open the printable sheet in a new tab, for previewing without
 * triggering the print dialog or minting a physical copy.
 *
 * This navigates to the character's /character/{slug}/print route rather
 * than writing a detached document into a blank window — that route fetches
 * its own data and mints its own QR code on load, so the resulting tab has
 * a real URL that refresh and browser back/forward work on correctly.
 */
export function previewCharacterSheet({ character }) {
    const slug = encodeCharacterId(character?.characterId);
    if (!slug) {
        toast.error("Cannot preview: this character hasn't been saved yet.");
        return;
    }
    // Opened synchronously (no await first) so browsers don't treat it as a blocked popup.
    const previewWindow = window.open(`/character/${slug}/print`, "_blank");
    if (!previewWindow) {
        toast.error("Preview was blocked by the browser. Please allow popups for this site and try again.");
    }
}
